import { useContext } from 'react';
import { Context as PlannerItemContext } from '@context/PlannerItemContext';
import Button from '@base/Button/Button';
import H5 from '@base/H5/H5';
import Image from '@base/Image';
import P2 from '@base/P2/P2';
import styled from '@emotion/styled';
import { Fonts, Spacings } from '@styles/index';
import boxShadowGenerator from '@utils/boxShadowGenerator';
import { getImage } from '@utils/imageHelper';
import pushRoute from '@utils/pushRoute';
import textHelper from '@utils/textHelper';
import { segment } from '@actions/analytics';
import { PLANNER_CARD_CLICKED, PLANNER_PAGE } from '@constants/segment';
import useGoalEventProps from '@hooks/useGoalEventProps';
import useMyInfo from '@api/hooks/auth/useMyInfo';
import { emptyList } from '@constants/empty';
import {
  browseTopSpacing,
  dayTitleLeftSpacing,
  plannerItemTypeHeightMap
} from '../../planner-constants';

const plannerHeroImages = {
  20: {
    0: {
      heroIcon: 'planner/renewal/renewal-save20-0.svg'
    },
    1: {
      heroIcon: 'planner/renewal/renewal-save20.svg'
    }
  },
  25: {
    0: {
      heroIcon: 'planner/renewal/renewal-save25-0.svg'
    },
    1: {
      heroIcon: 'planner/renewal/renewal-save25.svg'
    }
  },
  default: {
    0: {
      heroIcon: 'planner/renewal/renewal-save-0.svg'
    },
    1: {
      heroIcon: 'planner/renewal/renewal-save.svg'
    }
  }
};

const getHeroImage = (percentage = 'default', days_remaining) => {
  const daysRemaining = days_remaining > 0 ? 1 : 0;
  const computedPercentage = [20, 25].includes(percentage)
    ? percentage
    : 'default';
  const image = plannerHeroImages[computedPercentage][daysRemaining].heroIcon;
  return image;
};

const getBackgroundColor = (daysRemaining) => {
  return daysRemaining === 0
    ? 'rgba(246, 116, 116, 0.1)'
    : 'rgba(255, 173, 59, 0.15)';
};

const getCouponBackgroundImage = (daysRemaining) => {
  return daysRemaining === 0
    ? 'planner/renewal/coupon-background.svg'
    : 'planner/renewal/coupon-background-0.svg';
};

const getDaysRemaining = (daysRemaining) => {
  if (daysRemaining === 0) {
    return 'today';
  }
  if (daysRemaining === 1) {
    return 'tomorrow';
  }
  return `in ${daysRemaining} days`;
};

const getRenewalTitle = (percentage, daysRemaining) => {
  if (percentage >= 20) {
    return 'Extend now and save more';
  }
  return `Subscription ends ${getDaysRemaining(daysRemaining)}`;
};

const getRenewalSubtitle = (percentage, max_discount) => {
  if (percentage >= 20) {
    return `Get ${percentage}% off up to ₹${max_discount} if you extend today`;
  }
  return `Extend your subscription today for ${percentage}% off`;
};

const OuterWrapper = styled.div`
  padding-top: ${browseTopSpacing}px;
  height: ${({ $height }) => $height}px;
`;

const Wrapper = styled.div`
  margin-left: ${dayTitleLeftSpacing};
  box-shadow: ${boxShadowGenerator()};
  background-color: ${({ $daysRemaining }) =>
    getBackgroundColor($daysRemaining)};
  border-radius: ${Spacings.SPACING_2B};
  padding: ${Spacings.SPACING_4B} ${Spacings.SPACING_8B};
  padding-bottom: ${Spacings.SPACING_6B};
  height: 100%;
  display: flex;
  flex-direction: column;
`;

const ContentWrapper = styled.div`
  flex-grow: 1;
  background-repeat: no-repeat;
  background-position: center right;
  display: flex;
`;

const Title = styled(H5)`
  line-height: 129%;
  margin-bottom: ${Spacings.SPACING_1B};
`;

const Subtitle = styled(P2)`
  line-height: 133%;
  margin-top: ${Spacings.SPACING_1B};
`;

const CouponCode = styled(P2)`
  min-width: 136px;
  max-width: 45%;
  height: 32px;
  box-sizing: border-box;
  padding: ${Spacings.SPACING_2B} ${Spacings.SPACING_4B};
  background: ${({ $daysRemaining }) =>
    `url('${getImage(getCouponBackgroundImage($daysRemaining))}')`};
  background-size: cover;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const ExtendButton = styled(Button)`
  width: 120px;
  background-color: var(--color-base-fill);
  color: var(--color-text-tertiary);
  font-size: ${Fonts.P2};
`;

const ActionsContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-top: ${Spacings.SPACING_3B};
`;

const DescriptionContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  flex-grow: 1;
`;

const SubscriptionRenewalCard = ({ metaItem }) => {
  const { selectedGoalUID } = useContext(PlannerItemContext);
  const { code, days_remaining, percentage, max_discount } = metaItem.itemInfo;
  const heroImageSrc = getHeroImage(percentage, days_remaining);
  const { plannerItemType } = metaItem;
  const height = plannerItemTypeHeightMap[plannerItemType];
  const title = getRenewalTitle(percentage, days_remaining);
  const { myInfo = {} } = useMyInfo();
  const subscriptionInfo = (myInfo.latestUserSubscriptions || emptyList).find(
    ({ subscription }) => subscription?.value.uid === selectedGoalUID
  );
  const goalSegmentData = useGoalEventProps({ goalUID: selectedGoalUID });

  const handleClick = () => {
    segment.track(PLANNER_CARD_CLICKED, {
      ...goalSegmentData,
      last_primary_source: PLANNER_PAGE,
      last_primary_source_section: 'Planner',
      card_type: 'Plus Subscription Renewal'
    });
    const duration = textHelper.getHigherSubscription(
      Math.floor(subscriptionInfo.subscription.duration / 30)
    );
    pushRoute(
      {
        pathname: '/goal/[goalSlug]/[goalUID]/subscribe',
        query: { referral_code: encodeURIComponent(code || ''), duration }
      },
      `${
        subscriptionInfo.subscription.value.relative_link
      }/subscribe/?referral_code=${encodeURIComponent(
        code || ''
      )}&duration=${duration}`
    );
  };

  return (
    <OuterWrapper $height={height}>
      <Wrapper $daysRemaining={days_remaining}>
        <ContentWrapper>
          <DescriptionContainer>
            <Title>{title}</Title>
            <Subtitle color="var(--color-text-secondary)">
              {getRenewalSubtitle(percentage, max_discount)}
            </Subtitle>
          </DescriptionContainer>
          <Image alt={title} height="80" width="80" src={heroImageSrc} />
        </ContentWrapper>
        <ActionsContainer>
          {code && (
            <CouponCode $daysRemaining={days_remaining}>
              Use code <strong>&nbsp;{code}</strong>
            </CouponCode>
          )}
          <ExtendButton
            variant="filled"
            label="Extend"
            maxHeight="xs"
            onClick={handleClick}
          />
        </ActionsContainer>
      </Wrapper>
    </OuterWrapper>
  );
};

export default SubscriptionRenewalCard;
